import {
    DocuClipperBillingCycle,
    StripePlan,
} from "../../../../docuclipper/DocuclipperTypes";

export type PlanTierGroup = {
    parentPlan: string;
    displayName: string;
    tiers: TierOption[];
};

export type TierOption = {
    numPages: number;
    plans: {
        Monthly?: StripePlan;
        Annually?: StripePlan;
        AnnuallyBilledMonthly?: StripePlan;
    };
};

/**
 * Get the parent plan name from a plan
 * If parentPlan is set, use it. Otherwise, extract from the plan name.
 */
export const getParentPlanName = (plan: StripePlan): string => {
    if (plan.docuclipperPlan.parentPlan) {
        return plan.docuclipperPlan.parentPlan;
    }

    // Extract parent from name like "Starter 60" -> "Starter", "Business 1000" -> "Business"
    const name = plan.docuclipperPlan.name;

    // Handle known base plans
    if (["Starter", "Business", "Enterprise", "Professional"].includes(name)) {
        return name;
    }

    // Handle tiers like "Starter 60", "Business 1000", etc.
    const match = name.match(/^(Starter|Business|Enterprise|Professional)/);
    if (match) {
        return match[1];
    }

    return name;
};

/**
 * Group plans by parent plan and organize tiers
 * Hardcoded to show specific tiers for each plan
 */
export const groupPlansByTiers = (plans: StripePlan[]): PlanTierGroup[] => {
    // Define the specific tiers we want for each plan (ordered by pages ascending)
    const tierConfiguration = {
        Starter: [
            { name: "Starter 60", pages: 60 },
            { name: "Starter", pages: 120 },
            { name: "Starter 300", pages: 300 },
        ],
        Business: [
            { name: "Business", pages: 640 },
            { name: "Business 1000", pages: 1000 },
            { name: "Business 1500", pages: 1500 },
        ],
        Enterprise: [
            { name: "Enterprise", pages: 1800 },
            { name: "Enterprise 3000", pages: 3000 },
            { name: "Enterprise 5000", pages: 5000 },
        ],
    };

    const result: PlanTierGroup[] = [];

    // Process each parent plan
    Object.entries(tierConfiguration).forEach(([parentName, tierConfigs]) => {
        const tiers: TierOption[] = [];

        tierConfigs.forEach((config) => {
            // Find all plans that match this tier
            const matchingPlans = plans.filter((plan) => {
                const planName = plan.docuclipperPlan.name;
                const planPages = plan.docuclipperPlan.numPages;

                // Match by exact name or by pages and parent
                return (
                    (planName === config.name && planPages === config.pages) ||
                    (getParentPlanName(plan) === parentName && planPages === config.pages)
                );
            });

            if (matchingPlans.length > 0) {
                const tierOption: TierOption = {
                    numPages: config.pages,
                    plans: {},
                };

                // Organize by billing cycle
                matchingPlans.forEach((plan) => {
                    const billingCycle = plan.docuclipperPlan.billingCycle;
                    tierOption.plans[billingCycle] = plan;
                });

                // Only add tier if it has at least one billing cycle
                if (Object.keys(tierOption.plans).length > 0) {
                    tiers.push(tierOption);
                }
            }
        });

        // Add the parent plan group if it has tiers
        if (tiers.length > 0) {
            result.push({
                parentPlan: parentName,
                displayName: parentName,
                tiers,
            });
        }
    });

    return result;
};

/**
 * Get price for a specific tier and billing cycle
 */
export const getTierPrice = (
    tier: TierOption,
    billingCycle: DocuClipperBillingCycle
): number | null => {
    const plan = tier.plans[billingCycle];
    if (!plan) return null;

    // Return monthly equivalent
    if (billingCycle === "Monthly") {
        return plan.amount / 100;
    } else {
        // For annual plans, divide by 12 to get monthly equivalent
        return plan.amount / 100 / 12;
    }
};

/**
 * Get the plan features based on parent plan name
 */
export const getPlanFeatures = (parentPlan: string): { text: string; highlight?: boolean }[] => {
    const baseFeatures = {
        Starter: [
            { text: "Convert any Bank/CC Statement" },
            { text: "Transaction Categorization" },
            { text: "Convert any Invoice/Receipt" },
            { text: "Convert any Other Document" },
            { text: "Convert CSV files to QBO Online/Desktop" },
            { text: "Accounting Integrations" },
            // { text: "Batch Processing" },
            { text: "Customizable Output" },
            { text: "1-year Data Retention" },
        ],
        Business: [
            // { text: "All Starter features" },
            { text: "Unlimited Users" },
            { text: "Premium Support With Dedicated Account Manager" },
            { text: "Financial analysis (flow of funds, cashflow analysis ...)" },
            { text: "Fraud Detection" },
            { text: "Team Management & Collaboration" },
            { text: "Dedicated Queues For Faster Processing" },
            { text: "API Access" },
            { text: "2-year Data Retention" },
        ],
        Enterprise: [
            // { text: "All Business features" },
            // { text: "Custom number of pages", highlight: true },
            { text: "Custom number of pages" },
            { text: "Single Sign-On" },
            { text: "Audit Logs" },
            { text: "IP Whitelisting" },
            // { text: "Tailored Features" },
            { text: "5-year data retention" },
        ],
    };

    return baseFeatures[parentPlan] || [];
};

/**
 * Calculate savings percentage between monthly and annual billing
 */
export const calculateSavingsPercentage = (
    monthlyPrice: number,
    annualPrice: number
): number => {
    const annualAsMonthly = monthlyPrice * 12;
    const savings = annualAsMonthly - annualPrice;
    return Math.round((savings / annualAsMonthly) * 100);
};

/** Ordered page counts across all tiers (Starter → Business → Enterprise) for "next tier" logic */
const ORDERED_TIER_PAGES = [60, 120, 300, 640, 1000, 1500, 1800, 3000, 5000];

export type NextUpgradeOption = {
    plan: StripePlan;
    displayName: string;
    numPages: number;
    priceDisplay: string; // e.g. "$79/mo" or "$6.58/mo" (annual equivalent)
};

/**
 * Get the next tier up for the current plan (for "out of pages" upgrade CTA).
 * Returns null if already on the highest tier (e.g. Enterprise 5000).
 */
export const getNextUpgradeOption = (
    plans: StripePlan[],
    currentNumPages: number,
    billingCycle: DocuClipperBillingCycle
): NextUpgradeOption | null => {
    const groups = groupPlansByTiers(plans);
    if (groups.length === 0) return null;

    const currentIndex = ORDERED_TIER_PAGES.indexOf(currentNumPages);
    if (currentIndex < 0 || currentIndex >= ORDERED_TIER_PAGES.length - 1) {
        return null;
    }
    const nextPages = ORDERED_TIER_PAGES[currentIndex + 1];

    for (const group of groups) {
        for (const tier of group.tiers) {
            if (tier.numPages !== nextPages) continue;
            const plan = tier.plans[billingCycle] ?? tier.plans.Monthly ?? tier.plans.Annually ?? tier.plans.AnnuallyBilledMonthly;
            if (!plan) continue;
            const price = getTierPrice(tier, (plan.docuclipperPlan.billingCycle as DocuClipperBillingCycle) || billingCycle);
            const currencySymbol = plan.currency === "usd" ? "$" : plan.currency === "eur" ? "€" : plan.currency?.toUpperCase() || "$";
            const priceStr = price != null ? (price % 1 === 0 ? String(Math.round(price)) : price.toFixed(2)) : "";
            const priceDisplay = priceStr ? `${currencySymbol}${priceStr}/mo` : "";
            return {
                plan,
                displayName: plan.docuclipperPlan.name as string,
                numPages: nextPages,
                priceDisplay,
            };
        }
    }
    return null;
};

